/**
 * TanStack's `focusManager`, fed by the app coming to the foreground
 * (`D194`), with the access token warmed first.
 *
 * ── WHY NOT THE DEFAULT ────────────────────────────────────────────────────
 * The default listens to `visibilitychange`, which the WebView fires on a
 * resume — and every query older than its `staleTime` refetches at once
 * (`D190` measured 23 on one resume). After a break longer than the access
 * token's life, each of them then queued behind the one refresh the first
 * of them started. Here the resume asks the `ApiClient` to refresh FIRST
 * (`warmTokens`), and only then tells the manager the app is focused, so
 * the refetches go out with a live token. `onAppActiveChange` is
 * `@capacitor/app`'s `appStateChange` on the device and the page's
 * visibility on the web (`src/native`), so nothing is lost there.
 */

/** What this needs of TanStack's `FocusManager`. */
export interface FocusSink {
  setEventListener(
    setup: (setFocused: (focused?: boolean) => void) => (() => void) | undefined
  ): void;
}

export interface FocusSource {
  onAppActiveChange(handler: (active: boolean) => void): () => void;
  /** `api.warmTokens`: refresh if near expiry; never throws. */
  warmTokens(): Promise<void>;
}

/** Wire `sink` to `source`. Returns the teardown. */
export function wireFocus(sink: FocusSink, source: FocusSource): () => void {
  sink.setEventListener((setFocused) =>
    source.onAppActiveChange((active) => {
      if (!active) {
        setFocused(false);
        return;
      }
      source.warmTokens().then(
        () => setFocused(true),
        () => setFocused(true)
      );
    })
  );
  return () => {
    sink.setEventListener(() => undefined);
  };
}
